/* eslint-disable @typescript-eslint/indent */
import { ICategory } from '@pwskills/rachnaui/components/Navbar/Navbar.types';
import { useState, useEffect, useRef, useMemo } from 'react';
import {
  getRegistrationStatus,
  MASTERCLASS_REGISTRATION_AND_CLASS_STATUS,
} from '@/utils/common/masterclassDescription';
import { useRouter } from 'next/router';
import { MASTERCLASS_TYPES } from '@/utils/masterclass/masterclass';
import HomePageApi from '@/api/homePage';
import { IError } from '@/interfaces/common';
import ClockIcon from '@/assets/icons/ClockIcon';
import CalendarIcon from 'public/images/PostLogin/CalendarIcon';
import useMasterclassCountdownTimer from '@/hooks/useMasterclassCountdownTimer';
import TimeIcon from 'public/images/PostLogin/TimeIcon';
import PriceIcon from 'public/images/PostLogin/PriceIcon';
import { Tooltip, Typography } from '@pwskills/rachnaui';
import { getPriceDetails, getTimeFromDate } from '@/utils/helper/helperFunction';
import useMasterclassDescriptionStore from '@/store/MasterClassDescription/useMasterclasDescriptionStore';
import MetaSection from './MetaSection';
import WhatYouWillLearnSection from './WhatYouWillLearnSection';
import CertificateSection from './CertificateSection';
import MentorSection from './MentorSection';
import WhyYouShouldEnrollSection from './WhyYouShouldEnrollSection';
import Faq from './Faqa';
import StickyNavbar from './StickyNavbar';
import JoinMasterClassSection from './JoinMasterclassSection';
import HeroSection from './HeroSection';
import RelatedCoursesSection from './RelatedCourses';
import GlimpsesSection from './GlimpsesSection';
import HeroSectionDash from '../PostLoginOnboarding/MasterDescription/HeroSectionDash';
import RegistrationFlow from './PaymentFlow';

// import DottedLineWithCards from './GlimpsesSection/test';

const MasterclassDescriptionPage = ({
  masterclassDetails,
  isMasterclassBought,
  isClassTimerEnded,
}: {
  isClassTimerEnded: boolean;
  isMasterclassBought: boolean;
  masterclassDetails: any;
}) => {
  const [, setCategoryData] = useState<ICategory[]>([]);
  const [isFixed, setIsFixed] = useState(false);
  const section1Ref = useRef<HTMLDivElement>(null);
  const stickyContentRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const isDashBoard = router.pathname.includes('dashboard');
  const { type } = router.query;

  const { masterclassTimings, pricing, startDate, endDate } = masterclassDetails || {};

  // Use new API structure (startDate/endDate) or fallback to old structure (masterclassTimings)
  const { startTime, endTime } = masterclassTimings || {};
  const actualStartTime = startDate || startTime;
  const actualEndTime = endDate || endTime;
  const priceDetails = getPriceDetails(pricing) || {
    actualPrice: 0,
    currentPrice: 0,
    discount: 0,
    isCourseFree: true,
  };
  const setIsMasterclassBought = useMasterclassDescriptionStore(
    (state: { setIsMasterclassBought: (data: boolean) => void }) => state.setIsMasterclassBought
  );

  const { getDay, gethours, getMinutes, getSeconds } =
    useMasterclassCountdownTimer(actualStartTime);

  const days = getDay();
  const hours = gethours();
  const minutes = getMinutes();
  const seconds = getSeconds();

  const getMasterClassDuration = () => {
    if (!actualStartTime || !actualEndTime) {
      return 'Time TBD';
    }
    return `${getTimeFromDate(actualStartTime as string)} to ${getTimeFromDate(
      actualEndTime as string
    )} `;
  };
  const registrationStatus = getRegistrationStatus({
    startDate: actualStartTime as string,
    endDate: actualEndTime as string,
  });

  const fetchCategoryDetails = async () => {
    try {
      const catchResponse = await HomePageApi.getCompanyAndFaqs();

      if (catchResponse) {
        const categoriesDetails = catchResponse?.data?.categories;
        setCategoryData(categoriesDetails);
      }
    } catch (error: unknown) {
      // eslint-disable-next-line no-console
      console.error((error as IError)?.error);
    }
  };

  // not removing the code - we may need it again
  useEffect(() => {
    const handleScroll = () => {
      if (section1Ref.current && stickyContentRef.current) {
        const section1Rect = section1Ref.current.getBoundingClientRect();
        // const stickyContentHeight = stickyContentRef.current.offsetHeight;
        // section1Rect.height + stickyContentHeight - 50;
        if (window.scrollY > section1Rect.height / 2) {
          setIsFixed(true);
        } else {
          setIsFixed(false);
        }
      }
    };

    // Initial check to set the correct position
    handleScroll();

    // Add event listener
    window.addEventListener('scroll', handleScroll);

    // Clean up event listener
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    fetchCategoryDetails();
  }, []);
  const sideChipArray = useMemo(() => {
    return [
      {
        icon: <ClockIcon width="20" height="20" />,
        title: 'Starts In', // data.keyValueMetaInfo
        value: actualStartTime ? (
          <div className="flex gap-1 items-center">
            {days && <div>{days}</div>}
            {hours && <div>{hours}</div>}
            {minutes && <div>{minutes}</div>}
            {days && days === '0' && <div>{seconds}</div>}
          </div>
        ) : (
          'TBD'
        ),
      },
      {
        icon: <TimeIcon />,
        title: 'Time', // data.mode
        value: getMasterClassDuration(),
      },
      {
        icon: <CalendarIcon />,
        title: 'Date',
        value: actualStartTime
          ? new Date(actualStartTime).toLocaleDateString('en-US', {
              month: 'long', // e.g., January
              day: 'numeric', // e.g., 23
              year: 'numeric', // e.g., 2025
            })
          : 'Date TBD',
      },
      {
        icon: <PriceIcon />,
        title: 'Price',
        value: pricing?.IN ? `₹ ${pricing?.IN}` : 'Free',
      },
    ];
  }, [days, hours, minutes, seconds, actualStartTime, getMasterClassDuration, pricing]);

  return (
    <>
      <section ref={section1Ref} style={{ position: 'relative', zIndex: '99' }}>
        {isDashBoard ? (
          <HeroSectionDash
            registrationStatus={registrationStatus}
            isMasterclassBought={isMasterclassBought}
            isFixed={isFixed}
            priceDetails={priceDetails}
            setIsMasterclassBought={setIsMasterclassBought}
          />
        ) : (
          <HeroSection />
        )}
        {(masterclassDetails || isClassTimerEnded) && masterclassDetails._id && (
          <div
            ref={stickyContentRef}
            style={{
              width: '100%',
            }}
            className={`w-full ${
              isFixed
                ? ' bottom-0 medium:fixed large:fixed'
                : 'medium:fixed  medium:bottom-0  z-[4]'
            }`}
          >
            {!isDashBoard && <StickyNavbar isFixed={isFixed} />}
          </div>
        )}
      </section>
      {isMasterclassBought &&
        registrationStatus !== MASTERCLASS_REGISTRATION_AND_CLASS_STATUS.CLASS_ENDED && (
          <JoinMasterClassSection />
        )}
      {isDashBoard ? (
        <div className="grid grid-cols-1 large:grid-cols-12">
          <div className="order-2 large:order-1 col-span-12 large:col-span-9 relative">
            <div className="large:overflow-y-auto hide-scrollbar h-[calc(100vh-340px)]">
              <MetaSection />
              <GlimpsesSection />
              <WhatYouWillLearnSection />
              <CertificateSection />
              <MentorSection />
              <WhyYouShouldEnrollSection />
              {type !== MASTERCLASS_TYPES.MARKETING && <RelatedCoursesSection />}
              <Faq />
            </div>
          </div>
          <div className="order-1 lg:order-2 p-4 col-span-3 h-full lg:h-max-[400px]">
            <div className="p-4 w-full relative flex flex-col gap-4 bg-white rounded-lg shadow-medium justify-between">
              {sideChipArray.map((chip) => (
                <div className="flex items-center rounded-lg justify-between">
                  <div className="flex items-center gap-1">
                    {chip.icon}
                    <Typography component="body-small" variant="regular" className="text-gray-850">
                      {chip.title}
                    </Typography>
                  </div>

                  <Typography component="body-small" variant="regular" className="text-gray-850">
                    {chip.value}
                  </Typography>
                </div>
              ))}
              {/* <div
                className={`${
                  isFixed ? 'fixed bottom-2 left-0 right-0 p-4 z-30 w-full' : 'w-full'
                } `}
              > */}
              {isFixed ? (
                <div className="sticky bottom-2 z-30 inset-x-0 p-4">
                  <div className="container mx-auto">
                    <Tooltip text={registrationStatus}>
                      <RegistrationFlow
                        masterclassId={masterclassDetails?.masterclassId as string}
                        masterclassSlug={masterclassDetails?.slug as string}
                        masterclassName={masterclassDetails?.slug as string}
                        isUserRegistered={isMasterclassBought}
                        isFixed={isFixed}
                        registrationStatus={registrationStatus}
                        pricing={priceDetails}
                        setUserRegistered={setIsMasterclassBought}
                      />
                    </Tooltip>
                  </div>
                </div>
              ) : (
                <Tooltip text={registrationStatus}>
                  <RegistrationFlow
                    masterclassId={masterclassDetails?.masterclassId as string}
                    masterclassSlug={masterclassDetails?.slug as string}
                    masterclassName={masterclassDetails?.slug as string}
                    isUserRegistered={isMasterclassBought}
                    isFixed={isFixed}
                    registrationStatus={registrationStatus}
                    pricing={priceDetails}
                    setUserRegistered={setIsMasterclassBought}
                  />
                </Tooltip>
              )}
              {/* </div> */}
              {/* {isFixed ? (
                <div className="fixed bottom-2 left-0 right-0 p-4 z-30">
                  <Button size="small" className="w-full" onClick={() => alert(isFixed)}>
                    Register Now
                  </Button>
                </div>
              ) : ( */}
              {/* <Button size="small" className="w-full">
                Register Now
              </Button> */}
              {/* )} */}
            </div>
          </div>
        </div>
      ) : (
        <>
          <MetaSection />
          <GlimpsesSection showArrow />
          <WhatYouWillLearnSection />
          <CertificateSection />
          <MentorSection />
          <WhyYouShouldEnrollSection />
          {type !== MASTERCLASS_TYPES.MARKETING && <RelatedCoursesSection />}
          <Faq />
        </>
      )}
    </>
  );
};

export default MasterclassDescriptionPage;
